import * as Sentry from '@sentry/nextjs';

import { ContentTypes, getResponseCamelization } from '@/helpers/api';
import { delay } from '@/helpers/common';
import { REACT_APP_API_URL } from '@/helpers/conf';
import {
  API_PATH,
  CACHE_REVALIDATE_TIMEOUT,
  CLIENT_ENVS,
  INTERNAL_API_PATH,
  REQUEST_TIMEOUT,
} from '@/helpers/constants';
import { handleSignOutEvent } from '@/modules/common/helpers/handleSignOutEvent';
import type { RequestParams } from '@/types/api';

export interface ApiResponse<T> {
  data?: T;
  status?: number;
  statusText?: string;
  headers?: Headers;
  error?: Error | string;
}

interface RequestParamsExtended<T, TR> extends RequestParams {
  url: string;
  transformResponse?: (data: TR, headers?: Headers) => T;
}

function getStaticGlobalHeaders(accept: ContentTypes, data?: object): Headers {
  const headers = new Headers();
  headers.set('Accept', accept);
  !(data instanceof FormData) && headers.set('Content-Type', ContentTypes.json);

  return headers;
}

export async function request<T = any, TR = any>(
  requestParams: RequestParamsExtended<T, TR>
): Promise<ApiResponse<T>> {
  const {
    url,
    method = 'GET',
    data,
    functionScopedConfig,
    transformResponse,
    baseUrl,
    signal,
    accept = ContentTypes.vndJson,
    retries = 3,
    retryDelay = 2000,
  } = requestParams;

  const isInternalRequest = url.startsWith(INTERNAL_API_PATH);

  const requestGlobalConfig = {
    timeout: (REQUEST_TIMEOUT || 20) * 1000,
    headers: getStaticGlobalHeaders(accept, data),
    credentials: 'include',
  };
  // TODO: there is an issue with baseUrl on the client side
  const requestUrl = url.startsWith('http')
    ? url
    : `${
        baseUrl ||
        (CLIENT_ENVS.APP_ENV === 'development' || isInternalRequest
          ? ''
          : `${REACT_APP_API_URL}`)
      }${API_PATH}/${url}`;

  const urlWithParams =
    method === 'GET' && data
      ? `${requestUrl}?${new URLSearchParams(data as Record<string, string>).toString()}`
      : requestUrl;

  const requestHeaders: Headers = (() => {
    const globalHeaders = requestGlobalConfig.headers;

    if (functionScopedConfig?.headers) {
      new Headers(functionScopedConfig.headers).forEach((value, name) => {
        globalHeaders.set(name, value);
      });
    }

    return globalHeaders;
  })();

  const body = (() => {
    if (method === 'GET') return;
    return data instanceof FormData ? data : JSON.stringify(data);
  })();

  const config: RequestInit = {
    method,
    ...requestGlobalConfig,
    ...functionScopedConfig,
    signal,
    headers: requestHeaders,
    body,
    next: { revalidate: CACHE_REVALIDATE_TIMEOUT },
  };

  Sentry.addBreadcrumb({
    category: 'xhr',
    message: `Requesting ${urlWithParams}`,
    level: 'info',
    data: {
      method,
      headers: Array.from(requestHeaders.entries()),
      body,
    },
  });

  const makeRequest = async (attempt: number): Promise<ApiResponse<T>> => {
    try {
      const response = await fetch(urlWithParams, config);
      const { status, statusText, headers } = response;
      handleSignOutEvent(url, headers);
      const responseData = await response.json().catch(() => ({}));

      const transformedData = transformResponse
        ? transformResponse(responseData, headers)
        : getResponseCamelization(responseData, headers);

      const responseObj = {
        status,
        statusText,
        headers,
        data: transformedData,
      };

      if (!response.ok || transformedData?.errors) {
        Sentry.addBreadcrumb({
          category: 'xhr',
          message: `Failed request to ${requestUrl} with status ${status}`,
          level: 'warning',
          data: {
            url: requestUrl,
            method,
            status,
            statusText,
            transformedData,
          },
        });

        if (attempt < retries && status >= 500) {
          await delay(retryDelay);
          return makeRequest(attempt + 1);
        }

        return {
          ...responseObj,
          data: undefined,
          error: Object.keys(transformedData)
            ? transformedData.errors || transformedData
            : statusText,
        };
      }

      return responseObj;
    } catch (error: any) {
      Sentry.withScope((scope) => {
        Sentry.setTags({
          category: 'xhr',
          pathToFile: '/api/base.ts',
        });
        scope.setExtras({
          url: requestUrl,
          method,
        });

        scope.setLevel('error');

        Sentry.captureMessage('Error while submitting payment form');
      });

      return { error: error as Error };
    }
  };

  return makeRequest(0);
}
